import { useStore } from '@/services/layer-management-service/store'
import type { UserLayer, UserLayerTag } from '@onxmaps/layer-management'

const { userLayersMap, userLayerTagsMap, tagsToUserLayerIdsMap } = useStore()

/**
 * Gets a set of user layer ids for a given tag id.
 */
const getUserLayerIdsByTag = (tagId: string) => {
  return Array.from<string>(tagsToUserLayerIdsMap.value.get(tagId) ?? [])
}

/**
 * Gets an array of layers for a given tag id.
 */
const getUserLayersByTag = <T extends UserLayer>(tagId: string): T[] => {
  return getUserLayerIdsByTag(tagId)
    .map(getUserLayerById)
    .filter((l): l is T => !!l)
}

/**
 * Gets user layer by id.
 */
const getUserLayerById = <T extends UserLayer>(id: string): T | undefined => {
  return userLayersMap.value.get(id) as T
}

/**
 * Gets all user layers.
 */
const getUserLayers = (): UserLayer[] => {
  return Array.from(userLayersMap.value.values())
}

/**
 * Gets user layer tag by id.
 */
const getUserLayerTagById = (id: string): UserLayerTag | undefined => {
  return userLayerTagsMap.value.get(id)
}

/**
 * Gets user layer tags by type
 */
const getUserLayerTagsByType = (type: string): UserLayerTag[] => {
  const tags = []
  for (const tag of userLayerTagsMap.value.values()) {
    if (tag.type === type) {
      tags.push(tag)
    }
  }

  return tags
}

/**
 * *(AI-generated)*
 * Composable that exposes layer management utility functions.
 */
export const useLayers = () => {
  return {
    getUserLayerIdsByTag,
    getUserLayersByTag,
    getUserLayerById,
    getUserLayers,
    getUserLayerTagById,
    getUserLayerTagsByType,
  }
}

/**
 * Can be used to mock the useLayers composable return.
 */
export const useLayersMock = (): ReturnType<typeof useLayers> => {
  return {
    getUserLayerIdsByTag: jest.fn().mockReturnValue([]),
    getUserLayersByTag: jest.fn().mockReturnValue([]),
    getUserLayerById: jest.fn().mockReturnValue(undefined),
    getUserLayers: jest.fn().mockReturnValue([]),
    getUserLayerTagById: jest.fn().mockReturnValue(undefined),
    getUserLayerTagsByType: jest.fn().mockReturnValue([]),
  }
}
